import { db } from '../lib/firebase';
import { addDoc, collection, serverTimestamp, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { getStripe, createPaymentIntent, confirmPayment } from './stripeService';

// Database structure: vindie/{collection}

export interface DonationData {
  id: string;
  donorId: string;
  creatorId: string;
  contentId?: string;
  amount: number;
  message?: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  paymentMethod: string;
  transactionId?: string;
  createdAt: Date;
}

export interface SubscriptionData {
  id: string;
  userId: string;
  plan: 'free' | 'premium';
  status: 'active' | 'cancelled' | 'expired';
  startDate: Date;
  endDate?: Date;
  paymentMethod?: string;
  autoRenew: boolean;
}

export const processDonation = async (
  donorId: string,
  creatorId: string,
  amount: number,
  message?: string,
  contentId?: string,
  donorName?: string
): Promise<string> => {
  try {
    // Create payment intent with Stripe
    const clientSecret = await createPaymentIntent({
      amount: Math.round(amount * 100), // Convert to cents
      currency: 'usd',
      metadata: {
        creatorId,
        donorId,
        donorName: donorName || 'Anonymous',
        donorMessage: message || '',
        contentId: contentId || ''
      }
    });

    // Get Stripe instance
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe not loaded');
    }

    // For now, we'll simulate successful payment since we don't have card elements
    // In a real implementation, you would use Stripe Elements for card input
    // Simulate successful payment processing
    const transactionId = `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const donationData: Omit<DonationData, 'id'> = {
      donorId,
      creatorId,
      contentId,
      amount,
      message,
      status: 'completed',
      paymentMethod: 'stripe',
      transactionId,
      createdAt: new Date()
    };
    
    // Save donation to Firestore
    const docRef = await addDoc(collection(db, 'donations'), {
      ...donationData,
      createdAt: serverTimestamp()
    });
    
    // Update creator's earnings
    await addDoc(collection(db, 'earnings'), {
      creatorId,
      amount: amount * 0.9, // 90% to creator, 10% to platform
      platformFee: amount * 0.1,
      donationId: docRef.id,
      createdAt: serverTimestamp()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error processing donation:', error);
    throw error;
  }
};

export const getDonationStats = async (creatorId: string): Promise<{
  totalDonations: number;
  totalAmount: number;
  recentDonations: DonationData[];
}> => {
  try {
    const donationsQuery = query(
      collection(db, 'donations'),
      where('creatorId', '==', creatorId),
      where('status', '==', 'completed'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    
    const querySnapshot = await getDocs(donationsQuery);
    const donations: DonationData[] = [];
    let totalAmount = 0;
    
    querySnapshot.forEach((doc) => {
      const donation = { id: doc.id, ...doc.data() } as DonationData;
      donations.push(donation);
      totalAmount += donation.amount;
    });
    
    return {
      totalDonations: donations.length,
      totalAmount,
      recentDonations: donations.slice(0, 10)
    };
  } catch (error) {
    console.error('Error getting donation stats:', error);
    throw error;
  }
};

export const createSubscription = async (
  userId: string,
  plan: 'premium',
  paymentMethod: string
): Promise<string> => {
  try {
    const subscriptionData: Omit<SubscriptionData, 'id'> = {
      userId,
      plan,
      status: 'active',
      startDate: new Date(),
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
      paymentMethod,
      autoRenew: true
    };
    
    const docRef = await addDoc(collection(db, 'subscriptions'), {
      ...subscriptionData,
      startDate: serverTimestamp(),
      endDate: serverTimestamp()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

export const getSubscriptionStatus = async (userId: string): Promise<SubscriptionData | null> => {
  try {
    const subscriptionQuery = query(
      collection(db, 'subscriptions'),
      where('userId', '==', userId),
      where('status', '==', 'active'),
      orderBy('startDate', 'desc'),
      limit(1)
    );
    
    const querySnapshot = await getDocs(subscriptionQuery);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return { id: doc.id, ...doc.data() } as SubscriptionData;
  } catch (error) {
    console.error('Error getting subscription status:', error);
    throw error;
  }
};

export const cancelSubscription = async (subscriptionId: string): Promise<void> => {
  try {
    // In a real implementation, you would cancel the subscription with the payment provider
    // For now, we'll just update the status in Firestore
    await addDoc(collection(db, 'subscription_cancellations'), {
      subscriptionId,
      cancelledAt: serverTimestamp(),
      reason: 'user_requested'
    });
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error;
  }
};


